import { useEffect, useState } from "react";
import posthog from "posthog-js";
import {
  useFeatureFlagEnabled,
  useFeatureFlagPayload as usePostHogFeatureFlagPayload,
} from "posthog-js/react";
import { IS_TEST, POSTHOG_API_KEY, POSTHOG_HOST } from "../config/constants";
import {
  getCachedFeatureFlag,
  getCachedFeatureFlagPayload,
} from "../services/posthog";

const hasPostHogSettings = !!POSTHOG_API_KEY && !!POSTHOG_HOST;
const POSTHOG_FALLBACK_DELAY_MS = 3000;

type PostHogFeatureFlagsStatus = "loading" | "loaded" | "error";
type PostHogFeatureFlagsContext = { errorsLoading?: boolean };
type FeatureFlagResolution = {
  isEnabled: boolean;
  isLoading: boolean;
};

type AvailableFlags =
  | "bowtie-view"
  | "mahs-by-sce-page"
  | "barrier-layout-allowed"
  | "fuzzy-bowtie"
  | "flagged-questions"
  | "cto-mitigation-protocol"
  | "cto-sis-modification-assessment"
  | "cto-deferment"
  | "cto-date-selection-365-days"
  | "default-live-recommendations-filter"
  | "can-access-users-permission"
  | "tag-grouping-country-code";

const fallbackFeatureFlagRequests = new Map<
  AvailableFlags,
  Promise<boolean | undefined>
>();
const fallbackPayloadRequests = new Map<AvailableFlags, Promise<unknown>>();
const resolvedFeatureFlags = new Map<AvailableFlags, boolean>();
let featureFlagCacheGeneration = 0;

export const clearFeatureFlagCache = () => {
  featureFlagCacheGeneration += 1;
  fallbackFeatureFlagRequests.clear();
  fallbackPayloadRequests.clear();
  resolvedFeatureFlags.clear();
};

const getSharedFeatureFlagFallback = (flag: AvailableFlags) => {
  const cachedRequest = fallbackFeatureFlagRequests.get(flag);

  if (cachedRequest) {
    return cachedRequest;
  }

  const requestGeneration = featureFlagCacheGeneration;
  const request = Promise.resolve(getCachedFeatureFlag(flag)).catch((error) => {
    if (requestGeneration === featureFlagCacheGeneration) {
      fallbackFeatureFlagRequests.delete(flag);
    }
    throw error;
  });
  fallbackFeatureFlagRequests.set(flag, request);

  return request;
};

const getSharedFeatureFlagPayloadFallback = <PayloadType>(
  flag: AvailableFlags
) => {
  const cachedRequest = fallbackPayloadRequests.get(flag) as
    | Promise<PayloadType | undefined>
    | undefined;

  if (cachedRequest) {
    return cachedRequest;
  }

  const requestGeneration = featureFlagCacheGeneration;
  const request = Promise.resolve(
    getCachedFeatureFlagPayload<PayloadType>(flag)
  ).catch((error) => {
    if (requestGeneration === featureFlagCacheGeneration) {
      fallbackPayloadRequests.delete(flag);
    }
    throw error;
  });
  fallbackPayloadRequests.set(flag, request);

  return request;
};

const usePostHogFeatureFlagsStatus = () => {
  const [status, setStatus] = useState<PostHogFeatureFlagsStatus>("loading");

  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags?.(
      (_flags, _variants, context?: PostHogFeatureFlagsContext) => {
        setStatus(context?.errorsLoading ? "error" : "loaded");
      }
    );

    if (!unsubscribe) {
      setStatus("error");
    }

    return () => {
      unsubscribe?.();
    };
  }, []);

  return status;
};

const getInitialFeatureFlagResolution = (
  flag: AvailableFlags,
  isEnabled: boolean | undefined,
  flagOverrideCondition?: boolean
): FeatureFlagResolution => {
  const cachedValue = resolvedFeatureFlags.get(flag);
  const initialValue =
    cachedValue ?? isEnabled ?? flagOverrideCondition ?? false;

  return {
    isEnabled: initialValue,
    isLoading:
      !IS_TEST &&
      hasPostHogSettings &&
      cachedValue === undefined &&
      typeof isEnabled !== "boolean" &&
      typeof flagOverrideCondition !== "boolean",
  };
};

// flagOverrideCondition added to override feature flags when specific conditions apply.
// Currently hiding the bowtie view for MV32.
export const useFeatureFlagResolution = (
  flag: AvailableFlags,
  flagOverrideCondition?: boolean
): FeatureFlagResolution => {
  const isEnabled = useFeatureFlagEnabled(flag);
  const postHogFeatureFlagsStatus = usePostHogFeatureFlagsStatus();
  const [resolvedFlag, setResolvedFlag] = useState<FeatureFlagResolution>(
    getInitialFeatureFlagResolution(flag, isEnabled, flagOverrideCondition)
  );

  useEffect(() => {
    let isMounted = true;

    if (IS_TEST) {
      resolvedFeatureFlags.set(flag, true);
      setResolvedFlag({ isEnabled: true, isLoading: false });
      return () => {
        isMounted = false;
      };
    }

    if (typeof isEnabled === "boolean") {
      resolvedFeatureFlags.set(flag, isEnabled);
      setResolvedFlag({ isEnabled, isLoading: false });
      return () => {
        isMounted = false;
      };
    }

    if (!hasPostHogSettings || postHogFeatureFlagsStatus === "loaded") {
      const value =
        resolvedFeatureFlags.get(flag) ?? flagOverrideCondition ?? false;
      resolvedFeatureFlags.set(flag, value);
      setResolvedFlag({ isEnabled: value, isLoading: false });
      return () => {
        isMounted = false;
      };
    }

    const resolveFallback = () => {
      const requestGeneration = featureFlagCacheGeneration;

      getSharedFeatureFlagFallback(flag)
        .then((value) => {
          if (isMounted && requestGeneration === featureFlagCacheGeneration) {
            const resolvedValue = value ?? flagOverrideCondition ?? false;
            resolvedFeatureFlags.set(flag, resolvedValue);
            setResolvedFlag({ isEnabled: resolvedValue, isLoading: false });
          }
        })
        .catch(() => {
          if (isMounted && requestGeneration === featureFlagCacheGeneration) {
            const value = flagOverrideCondition ?? false;
            resolvedFeatureFlags.set(flag, value);
            setResolvedFlag({ isEnabled: value, isLoading: false });
          }
        });
    };

    if (postHogFeatureFlagsStatus === "error") {
      resolveFallback();
      return () => {
        isMounted = false;
      };
    }

    const fallbackTimeout = window.setTimeout(() => {
      resolveFallback();
    }, POSTHOG_FALLBACK_DELAY_MS);

    return () => {
      isMounted = false;
      window.clearTimeout(fallbackTimeout);
    };
  }, [flag, flagOverrideCondition, isEnabled, postHogFeatureFlagsStatus]);

  return resolvedFlag;
};

export const useFeatureFlag = (
  flag: AvailableFlags,
  flagOverrideCondition?: boolean
) => {
  const { isEnabled } = useFeatureFlagResolution(flag, flagOverrideCondition);

  return isEnabled;
};

export const useFeatureFlagPayload = <PayloadType>(
  flag: AvailableFlags,
  fallbackValue: PayloadType
): PayloadType => {
  const isEnabled = useFeatureFlagEnabled(flag);
  const payload = usePostHogFeatureFlagPayload(flag) as PayloadType | undefined;
  const postHogFeatureFlagsStatus = usePostHogFeatureFlagsStatus();
  const [resolvedPayload, setResolvedPayload] =
    useState<PayloadType>(fallbackValue);

  useEffect(() => {
    let isMounted = true;

    if (IS_TEST) {
      setResolvedPayload(fallbackValue);
      return () => {
        isMounted = false;
      };
    }

    if (payload !== undefined) {
      setResolvedPayload(payload);
      return () => {
        isMounted = false;
      };
    }

    if (typeof isEnabled === "boolean") {
      setResolvedPayload(fallbackValue);
      return () => {
        isMounted = false;
      };
    }

    if (!hasPostHogSettings || postHogFeatureFlagsStatus === "loaded") {
      setResolvedPayload(fallbackValue);
      return () => {
        isMounted = false;
      };
    }

    const resolveFallback = () => {
      const requestGeneration = featureFlagCacheGeneration;

      getSharedFeatureFlagPayloadFallback<PayloadType>(flag)
        .then((value) => {
          if (isMounted && requestGeneration === featureFlagCacheGeneration) {
            setResolvedPayload(value ?? fallbackValue);
          }
        })
        .catch(() => {
          if (isMounted && requestGeneration === featureFlagCacheGeneration) {
            setResolvedPayload(fallbackValue);
          }
        });
    };

    if (postHogFeatureFlagsStatus === "error") {
      resolveFallback();
      return () => {
        isMounted = false;
      };
    }

    const fallbackTimeout = window.setTimeout(() => {
      resolveFallback();
    }, POSTHOG_FALLBACK_DELAY_MS);

    return () => {
      isMounted = false;
      window.clearTimeout(fallbackTimeout);
    };
  }, [fallbackValue, flag, isEnabled, payload, postHogFeatureFlagsStatus]);

  return resolvedPayload;
};

export const useFeatureFlagPayloadHook = useFeatureFlagPayload;
